// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';
import * as log from './log';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// Convert a base64 data URL string (as returned by the photoroom-cleanup
// endpoint) into a File object, so it can be handed straight to
// `sdk.images.upload({ image: file }, ...)`.
const dataUrlToFile = (dataUrl, originalFilename) => {
  const [header, base64Data] = dataUrl.split(',');
  const mimeMatch = header.match(/data:(.*?);base64/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const binary = atob(base64Data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  // PhotoRoom always returns PNG, so swap the extension to match.
  const filename = (originalFilename || 'photo.jpg').replace(/\.[^/.]+$/, '.png');
  return new File([bytes], filename, { type: mime });
};

// Send a listing photo through our PhotoRoom cleanup proxy (background
// removal + studio finish) before it gets uploaded to Sharetribe.
//
// Resolves with a cleaned-up File on success, rejects otherwise (there is
// no fallback to the original photo - see uploadImageThunk for why).
//
// NOTE: this intentionally calls `window.fetch` directly instead of going
// through the shared `request()` helper above. `request()` only attaches
// the body when it's being Transit-serialized (see `shouldSerializeBody`);
// any other body - including a raw multipart FormData like this one - gets
// silently dropped, which is what caused the original "Failed to upload
// photo" bug in production (the server received a request with no file at
// all, since nothing was actually being sent).
//
// See `server/api/photoroom-cleanup.js` for the server-side proxy.
export const cleanupListingPhoto = file => {
  const formData = new FormData();
  formData.append('imageFile', file, file.name);

  const url = `${apiBaseUrl()}/api/photoroom-cleanup`;

  return window
    .fetch(url, {
      method: methods.POST,
      credentials: 'include',
      // No Content-Type header here on purpose - the browser sets
      // 'multipart/form-data; boundary=...' itself when the body is a
      // FormData instance. Setting it manually breaks the boundary.
      body: formData,
    })
    .then(res => {
      if (!res.ok) {
        return res.json().then(data => {
          const e = new Error((data && data.error) || 'Photo cleanup failed.');
          throw Object.assign(e, data);
        });
      }
      return res.json();
    })
    .then(({ imageBase64 }) => dataUrlToFile(imageBase64, file.name));
};

// Dhobara: generates a listing's PDP description from its Step 2 fields via
// the Claude API. Called invisibly from EditListingDetailsPanel.js's onSubmit
// - the seller never sees this request or its result; it's saved straight to
// the listing's `description` attribute. Returns null (instead of throwing)
// on failure so a description-generation hiccup never blocks Step 2 from
// saving - the listing just keeps whatever description it already had.
export const generateListingDescription = fields => {
  const url = `${apiBaseUrl()}/api/generate-description`;

  return window
    .fetch(url, {
      method: methods.POST,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields }),
    })
    .then(res => (res.ok ? res.json() : Promise.reject(res)))
    .then(({ description }) => description)
    .catch(e => {
      log.error(e, 'generate-description-failed');
      return null;
    });
};

// Dhobara: screens a listing's photos for visual signs of designer
// authenticity via the Claude API (vision). Called invisibly from
// EditListingDetailsPanel.js's onSubmit, alongside generateListingDescription
// - the seller never sees this request or the model's reasoning, only the
// resulting "Authentic Designer Tags" badge on the listing page (and only
// when verified is true). Returns null (instead of throwing) on failure so
// a check hiccup never blocks Step 2 from saving - the listing just keeps
// whatever authenticity state it already had.
export const checkListingAuthenticity = ({ designer, garmentType, imageUrls }) => {
  const url = `${apiBaseUrl()}/api/check-authenticity`;

  return window
    .fetch(url, {
      method: methods.POST,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ designer, garmentType, imageUrls }),
    })
    .then(res => (res.ok ? res.json() : Promise.reject(res)))
    .then(({ verified, confidence }) => ({ verified: !!verified, confidence: confidence || null }))
    .catch(e => {
      log.error(e, 'check-authenticity-failed');
      return null;
    });
};

// Dhobara: sends the seller a "your listing is live" confirmation email via
// SendGrid, right after a new listing is published (EditListingPage.duck.js's
// publishListingPayloadCreator). Fire-and-forget from the caller's
// perspective - failure here (missing API key, SendGrid error, etc.) is
// logged but never throws, so it can never block or delay the publish flow.
export const sendListingLiveEmail = ({ toEmail, sellerName, listingTitle, listingUrl }) => {
  const url = `${apiBaseUrl()}/api/send-listing-live-email`;

  return window
    .fetch(url, {
      method: methods.POST,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ toEmail, sellerName, listingTitle, listingUrl }),
    })
    .then(res => (res.ok ? res.json() : Promise.reject(res)))
    .catch(e => {
      log.error(e, 'send-listing-live-email-failed');
      return null;
    });
};

// Dhobara: fires the automated listing-review agent for a just-published
// listing (server/api/review-listing.js). Fire-and-forget - never throws -
// so a hiccup can't block the publish flow. The agent approves / rejects /
// holds the listing server-side; the events-poll is the safety net if this
// call is skipped or fails.
export const submitListingForReview = ({ listingId }) => {
  const url = `${apiBaseUrl()}/api/review-listing`;

  return window
    .fetch(url, {
      method: methods.POST,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ listingId }),
    })
    .then(res => (res.ok ? res.json() : Promise.reject(res)))
    .catch(e => {
      log.error(e, 'submit-listing-for-review-failed');
      return null;
    });
};

// Dhobara: creates a UPS shipping label via ShipStation the instant
// checkout's payment is confirmed (CheckoutPage.duck.js's
// confirmPaymentPayloadCreator). Fire-and-forget from the caller's
// perspective - failure here (missing API keys, ShipStation error, missing
// seller address, etc.) is logged but never throws, so it can never block
// or delay checkout.
export const createShippingLabel = ({ transactionId }) => {
  const url = `${apiBaseUrl()}/api/shipstation-create-label`;

  return window
    .fetch(url, {
      method: methods.POST,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactionId }),
    })
    .then(res => (res.ok ? res.json() : Promise.reject(res)))
    .catch(e => {
      log.error(e, 'shipstation-create-label-failed');
      return null;
    });
};

// Fetches the current logged-in user's spendable Dhobara store-credit
// balance (in cents), for display in the Buyer dashboard tab. Resolves to
// { balanceCents: 0 } rather than throwing on any failure (logged-out user,
// network error) since this is a supplementary display, not a blocking read.
//
// See `server/api/my-credit-balance.js`.
export const fetchMyCreditBalance = () => {
  const url = `${apiBaseUrl()}/api/my-credit-balance`;

  return window
    .fetch(url, {
      method: methods.GET,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
    .then(res => (res.ok ? res.json() : Promise.reject(res)))
    .catch(e => {
      log.error(e, 'fetch-my-credit-balance-failed');
      return { balanceCents: 0 };
    });
};
